<!-- src/components/common/Button.svelte -->
<script>
  import { router } from '../../lib/router.js';
  
  /**
   * Button component with different variants and sizes
   * @param {string} variant - The button variant: 'primary', 'secondary', or 'outline'
   * @param {string} size - The button size: 'small', 'medium', or 'large'
   * @param {('button'|'submit'|'reset')} type - The button type
   * @param {boolean} disabled - Whether the button is disabled
   * @param {string} href - Optional href for link-style buttons
   * @param {boolean} external - Force external link behavior (new tab, no SPA routing)
   */
  export let variant = 'primary';
  export let size = 'medium';
  export let type = 'button';
  export let disabled = false;
  export let href = null;
  export let external = false;

  // Treat absolute http(s) links (e.g. a Calendly scheduler) as external:
  // open in a new tab and let the browser handle them instead of the SPA router.
  $: is_external = external || (typeof href === 'string' && /^https?:\/\//.test(href));

  const button_classes = [
    'button',
    `button--${variant}`,
    `button--${size}`
  ].join(' ');

  function handle_click(e) {
    if (disabled) return;
    if (href && !is_external) {
      e.preventDefault();
      router.navigate(href);
    }
  }
</script>

{#if href}
  <a
    {href}
    class={button_classes}
    class:disabled
    target={is_external ? '_blank' : undefined}
    rel={is_external ? 'noopener noreferrer' : undefined}
    on:click={handle_click}
  >
    <slot />
  </a>
{:else}
  <button
    {type}
    class={button_classes}
    class:disabled
    {disabled}
    on:click
  >
    <slot />
  </button>
{/if}

<style>
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    outline: none;
  }

  /* Variants */
  .button--primary {
    background: var(--color-primary);
    color: var(--color-background);
  }

  .button--primary:hover:not(.disabled) {
    background: var(--color-secondary);
  }

  .button--secondary {
    background: var(--color-card-bg);
    color: var(--color-text);
  }

  .button--secondary:hover:not(.disabled) {
    background: var(--color-card-hover);
  }

  .button--outline {
    background: transparent;
    border: 2px solid var(--color-primary);
    color: var(--color-primary);
  }

  .button--outline:hover:not(.disabled) {
    background: var(--color-primary);
    color: var(--color-background);
  }

  /* Sizes */
  .button--small {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  .button--medium {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }

  .button--large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  /* States */
  .disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style> 